// ⚠️ ATTENZIONE — File blindato §391 (2026-06-04)
// 🔒 §-FASE-4C 2026-09-26 — autorizzazione founder: retention referti/pazienti riallineata all'Archivio
// operativo (nessuna conservazione decennale generica). Vedi docs/legal/FASE_4C_REPORT.md.
// NON modificare senza autorizzazione esplicita del founder.
// Ogni claim qui riportato deve corrispondere a ciò che il sistema realmente
// implementa e può supportare in audit/contestazione legale. Nessuna esagerazione
// marketing. Ogni modifica deve essere accompagnata da un § numerato e testata.
// 🔒 §-LEGAL-ALIGN 2026-09-07 — autorizzazione founder esplicita: rimosso claim assoluto
// "Progettato secondo il GDPR"; audio allineato all'Informativa (temporaneo fino alla finalizzazione, non
// "non conservato"); backup: retention del provider Supabase (Blocco 2). Vedi docs/LEGAL_DOCUMENTS_FINAL_ALIGNMENT.md.
import * as React from 'react';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RefertusLogo } from '@/components/ui/RefertusLogo';
import { Badge } from '@/components/ui/badge';
import { 
  Shield, 
  Lock, 
  Server, 
  FileCheck, 
  Eye, 
  Trash2, 
  Download, 
  Bell,
  Clock,
  CheckCircle2,
  ArrowRight,
  Building2,
  FileText,
  KeyRound,
  Database,
  Globe,
  AlertTriangle,
  HardDrive,
  Fingerprint,
  ScrollText,
  Gavel,
  HeartPulse,
  UserCheck,
  ShieldCheck,
  Mail,
} from 'lucide-react';
import { useNavigate } from '@/lib/router-compat';
import { Link } from '@/lib/router-compat';
import { PublicFooter } from '@/components/PublicFooter';

const certifications = [
  {
    name: 'GDPR',
    status: 'Reg. UE 2016/679',
    description: 'Trattamento impostato secondo il Regolamento UE 2016/679; il GDPR non prevede una certificazione di conformità generale',
    icon: FileCheck,
    badge: 'compliant'
  },
  {
    name: 'Dati sanitari',
    status: 'Art. 9 GDPR',
    description: 'Categorie particolari trattate con basi giuridiche dedicate (cura, obbligo legale)',
    icon: HeartPulse,
    badge: 'compliant'
  },
  {
    name: 'Hosting UE',
    status: 'Attivo',
    description: 'Regione primaria del progetto in UE; alcuni fornitori possono comportare trattamenti extra-UE',
    icon: Server,
    badge: 'certified'
  },
  {
    name: 'Certificazioni Refertius',
    status: 'Nessuna dichiarata',
    description: 'Le certificazioni dei provider non costituiscono certificazioni proprie di Refertius',
    icon: Shield,
    badge: 'in-progress'
  },
];

const gdprRights = [
  {
    title: 'Diritto di Accesso',
    article: 'Art. 15 GDPR',
    description: 'I pazienti possono richiedere copia di tutti i loro dati personali trattati.',
    icon: Eye,
    implementation: 'Export completo dei dati in formato leggibile (JSON/PDF) disponibile in qualsiasi momento.'
  },
  {
    title: 'Diritto di Rettifica',
    article: 'Art. 16 GDPR',
    description: 'I pazienti possono richiedere la correzione di dati inesatti.',
    icon: FileText,
    implementation: 'Modifica immediata dei dati anagrafici e clinici tramite interfaccia dedicata.'
  },
  {
    title: 'Diritto alla Cancellazione',
    article: 'Art. 17 GDPR',
    description: 'Diritto all\'oblio con cancellazione completa dei dati.',
    icon: Trash2,
    implementation: 'Cancellazione irreversibile con conferma scritta, nel rispetto degli obblighi di conservazione sanitaria.'
  },
  {
    title: 'Diritto alla Portabilità',
    article: 'Art. 20 GDPR',
    description: 'I pazienti possono ricevere i loro dati in formato strutturato.',
    icon: Download,
    implementation: 'Export in formati standard (JSON, CSV, PDF) per trasferimento ad altri sistemi.'
  },
  {
    title: 'Diritto di Opposizione',
    article: 'Art. 21 GDPR',
    description: 'I pazienti possono opporsi al trattamento dei loro dati.',
    icon: AlertTriangle,
    implementation: 'Gestione delle opposizioni con sospensione immediata del trattamento non essenziale.'
  },
  {
    title: 'Diritto alla Limitazione',
    article: 'Art. 18 GDPR',
    description: 'I pazienti possono richiedere la limitazione del trattamento.',
    icon: Lock,
    implementation: 'Flag di limitazione che blocca elaborazioni non autorizzate.'
  },
];

const securityMeasures = [
  {
    category: 'Crittografia',
    icon: KeyRound,
    measures: [
      { name: 'Crittografia at-rest', detail: 'AES-256 sul database e sugli oggetti archiviati (standard provider EU)' },
      { name: 'Crittografia in-transit', detail: 'TLS 1.2+ su tutte le comunicazioni client e service-to-service' },
      { name: 'Integrità referti', detail: 'Hash SHA-256 + timestamp + ID medico stampati su ogni referto firmato' },
      { name: 'Versioning immutabile', detail: 'Ogni modifica al referto firmato genera una versione append-only non cancellabile' },
    ]
  },
  {
    category: 'Infrastruttura',
    icon: Server,
    measures: [
      { name: 'Data Center UE', detail: 'Database e storage in Unione Europea (Parigi)' },
      { name: 'Backup gestiti', detail: 'Backup giornalieri Supabase; PITR non attivo' },
      { name: 'Disaster Recovery', detail: 'Point-in-time recovery a livello provider, ripristino su richiesta' },
      { name: 'Audio temporaneo', detail: 'Eliminazione automatica entro 24 ore dalla creazione' },
    ]
  },
  {
    category: 'Controllo Accessi',
    icon: Fingerprint,
    measures: [
      { name: '2FA amministratori', detail: '2FA obbligatoria per gli operatori Refertius con accesso ai pannelli admin' },
      { name: 'SSO/SAML enterprise', detail: 'Disponibile per cliniche e strutture su piani enterprise' },
      { name: 'RBAC', detail: 'Ruoli separati (admin, medico, viewer) con policy a livello di database (RLS)' },
      { name: 'Isolamento dati', detail: 'Row-Level Security su ogni tabella sensibile: ogni medico vede solo i propri dati' },
    ]
  },
  {
    category: 'Monitoraggio',
    icon: Bell,
    measures: [
      { name: 'Audit log', detail: 'Tracciamento degli accessi e delle azioni sensibili (login, modifiche referto, export)' },
      { name: 'Alerting errori', detail: 'Monitoraggio runtime con notifiche immediate su errori e anomalie' },
      { name: 'Scanner sicurezza continuo', detail: 'Linter database + scanner edge functions eseguiti automaticamente ad ogni rilascio' },
      { name: 'Rate limiting', detail: 'Protezione anti-brute-force su autenticazione e endpoint sensibili' },
    ]
  },
];

const dataRetention = [
  { type: 'Referti medici (Archivio operativo)', retention: 'Durata del rapporto', basis: 'Trattati per conto del Titolare; alla cessazione restituzione e cancellazione secondo istruzioni del Titolare e politica di conservazione. La conservazione sanitaria prevista dalla normativa resta in capo al medico/struttura' },
  { type: 'Dati anagrafici pazienti', retention: 'Durata del rapporto', basis: 'Collegati ai referti, stesso regime' },
  { type: 'Audio registrazioni', retention: 'Entro 24 ore dalla creazione', basis: 'Cancellazione automatica delle registrazioni' },
  { type: 'Trascrizioni testuali', retention: 'Collegate al referto', basis: 'Necessarie per generazione e verifica referto' },
  { type: 'Log di accesso', retention: '24 mesi', basis: 'Sicurezza e accountability (allineato all’Informativa Privacy); da validare nella politica di conservazione' },
  { type: 'Dati di fatturazione', retention: '10 anni', basis: 'Obbligo fiscale e contabile (Art. 2220 c.c.) — trattamento proprio di Refertius' },
  { type: 'Backup del database', retention: 'Retention del provider secondo il piano del progetto', basis: 'Backup fisici giornalieri gestiti da Supabase; PITR non attivo; file Storage non inclusi' },
];

const documents = [
  { name: 'Informativa Privacy', description: 'Informativa completa Art. 13-14 GDPR', downloadable: true, link: '/privacy' },
  { name: 'DPIA - Valutazione d\'Impatto', description: 'Data Protection Impact Assessment', downloadable: true, link: '/gdpr/dpia' },
  { name: 'Registro dei Trattamenti', description: 'Registro conforme Art. 30 GDPR', downloadable: true, link: '/gdpr/registro-trattamenti' },
  { name: 'DPA - Accordo Trattamento Dati', description: 'Data Processing Agreement per cliniche', downloadable: true, link: '/gdpr/dpa' },
  { name: 'Procedura Data Breach', description: 'Piano di risposta alle violazioni dati', downloadable: true, link: '/gdpr/data-breach' },
  { name: 'Informativa Audio Recording', description: 'Informativa pazienti per registrazione audio', downloadable: true, link: '/gdpr/informativa-audio' },
  { name: 'Cookie Policy', description: 'Policy sui cookie e tracciamento', downloadable: true, link: '/cookie' },
  { name: 'Termini di Servizio', description: 'Condizioni generali del servizio', downloadable: true, link: '/termini' },
];

export default function Compliance() {
  const navigate = useNavigate();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead />
      <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div onClick={() => navigate('/')} className="cursor-pointer">
            <RefertusLogo size="sm" />
          </div>
          <button 
            onClick={() => navigate('/')} 
            className="text-sm font-medium text-muted-foreground hover:text-muted-foreground transition-colors"
          >
            ← Torna alla home
          </button>
        </div>
      </header>

      {/* Hero — austere, no gradient */}
      <section className="py-12 sm:py-20 px-4 border-b border-border">
        <div className="container mx-auto max-w-5xl text-center">
          <div className="inline-flex items-center gap-2 border border-border text-muted-foreground px-4 py-2 rounded-sm text-xs uppercase tracking-wider mb-6">
            <ShieldCheck className="h-3.5 w-3.5" />
            Reg. UE 2016/679 · Trattamento dati sanitari
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-light text-muted-foreground mb-4 tracking-tight">
            Sicurezza e Conformità
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Architettura di sicurezza multi-livello per dati sanitari. 
            Crittografia AES-256, hosting EU, audit trail completo.
          </p>
        </div>
      </section>

      {/* Certifications — monochrome cards */}
      <section className="py-10 sm:py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Standard e certificazioni</p>
            <h2 className="text-2xl sm:text-3xl font-light tracking-tight">Certificazioni e Conformità</h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {certifications.map((cert) => (
              <Card key={cert.name} className="p-4 sm:p-6 text-center bg-card border-border">
                <Badge 
                  variant="outline"
                  className={`mb-2 text-[11px] font-normal tracking-wide ${
                    cert.badge === 'certified' ? 'border-foreground/30 text-muted-foreground' :
                    cert.badge === 'compliant' ? 'border-foreground/30 text-muted-foreground' :
                    'border-muted-foreground/30 text-muted-foreground'
                  }`}
                >
                  {cert.status}
                </Badge>
                <h3 className="font-medium text-sm sm:text-base mb-1">{cert.name}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{cert.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* GDPR Rights — clean, no colored backgrounds */}
      <section className="py-10 sm:py-16 px-4 border-y border-border bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Regolamento UE 2016/679</p>
            <h2 className="text-2xl sm:text-3xl font-light tracking-tight mb-3">Diritti GDPR Garantiti</h2>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              Esercizio completo dei diritti previsti per pazienti e interessati
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {gdprRights.map((right) => (
              <Card key={right.title} className="p-4 sm:p-5 bg-card">
                <h3 className="font-medium text-sm mb-0.5">{right.title}</h3>
                <span className="text-[10px] text-muted-foreground tracking-wide">{right.article}</span>
                <p className="text-xs text-muted-foreground mt-1.5 mb-2">{right.description}</p>
                <div className="border border-border rounded-sm p-2 bg-muted/30">
                  <p className="text-xs text-muted-foreground/70">
                    <span>{right.implementation}</span>
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Security Measures — technical grid */}
      <section className="py-10 sm:py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Misure tecniche e organizzative</p>
            <h2 className="text-2xl sm:text-3xl font-light tracking-tight">Infrastruttura di Sicurezza</h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            {securityMeasures.map((category) => (
              <Card key={category.category} className="p-4 sm:p-6 bg-card">
                <div className="mb-4 pb-3 border-b border-border">
                  <h3 className="text-base sm:text-lg font-normal tracking-tight">{category.category}</h3>
                </div>
                <ul className="space-y-3">
                  {category.measures.map((measure) => (
                    <li key={measure.name} className="flex items-start gap-2">
                      <div className="w-1 h-1 rounded-full bg-foreground/30 shrink-0 mt-2" />
                      <div>
                        <p className="font-normal text-muted-foreground text-sm">{measure.name}</p>
                        <p className="text-xs text-muted-foreground">{measure.detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Data Retention — clean table */}
      <section className="py-10 sm:py-16 px-4 border-y border-border bg-muted/30">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Policy di conservazione</p>
            <h2 className="text-2xl sm:text-3xl font-light tracking-tight mb-3">Tempi di Conservazione Dati</h2>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              Conservazione minima nel rispetto degli obblighi normativi
            </p>
          </div>

          <Card className="overflow-hidden border-border">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[500px]">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                     <th className="text-left p-3 sm:p-4 text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-normal">Tipologia</th>
                    <th className="text-left p-3 sm:p-4 text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-normal">Retention</th>
                    <th className="text-left p-3 sm:p-4 text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-normal">Base giuridica</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {dataRetention.map((item) => (
                    <tr key={item.type} className="hover:bg-muted/30">
                      <td className="p-3 sm:p-4 font-normal text-sm">{item.type}</td>
                      <td className="p-3 sm:p-4">
                        <span className="text-xs bg-muted px-2 py-0.5 rounded-sm">{item.retention}</span>
                      </td>
                      <td className="p-3 sm:p-4 text-muted-foreground text-xs">{item.basis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </section>

      {/* Data Processing */}
      <section className="py-10 sm:py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Trattamento dati</p>
            <h2 className="text-2xl sm:text-3xl font-light tracking-tight">Dati e Localizzazione</h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            <Card className="p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-normal mb-4 pb-3 border-b border-border">
                Dati trattati
              </h3>
              <ul className="space-y-2">
                {[
                  'Dati anagrafici pazienti (nome, cognome, codice fiscale)',
                  'Dati di contatto (email, telefono — opzionali)',
                  'Audio della visita (temporaneo, eliminato entro 24 ore)',
                  'Trascrizioni testuali generate da Refertius',
                  'Referti medici firmati',
                  'Log di accesso e utilizzo',
                  'Dati del medico e della struttura'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-xs sm:text-sm">
                    <div className="w-1 h-1 rounded-full bg-foreground/30 shrink-0 mt-2" />
                    {item}
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-normal mb-4 pb-3 border-b border-border">
                Localizzazione e trasferimenti
              </h3>
              <ul className="space-y-2">
                {[
                  'Database e storage primari: Unione Europea (Parigi)',
                  'Backup giornalieri gestiti da Supabase (retention del provider secondo il piano del progetto); PITR non attivo',
                  'Resend: link al paziente di 45 giorni protetto dalla data di nascita, senza PDF allegato; copia al medico con allegato; DPA nei termini (31/12/2025), SCC + EU-US DPF',
                  'Trascrizione: Deepgram (primaria) e OpenAI (riserva); garanzie nel DPA 2.0',
                  'Percorso clinico: OpenAI e Anthropic, con accordo sul trattamento dei dati (art. 28 GDPR) e SCC',
                  'Stripe: provider distinto per pagamenti, senza dati clinici',
                  'Sentry: monitoraggio degli errori tecnici del browser, configurato per non ricevere contenuti clinici',
                  'Teleconsulto: QR e data di nascita, risposte trascritte e riassunte anche con IA, decisione del medico. Audio risposte: entro 7 giorni dalla firma del referto e, in ogni caso, entro 30 giorni dalla chiusura del teleconsulto; audio visite: entro 24 ore. Trascrizione e referto restano nell’archivio. Televisita: adesione rispondendo, controllo di diagnosi formulata in presenza, non prima visita né emergenze; modalità a distanza nel referto (Indicazioni nazionali 17/12/2020). Aggiornamento 30 ottobre 2026; elenco completo e garanzie nel DPA 2.0'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-xs sm:text-sm">
                    <div className="w-1 h-1 rounded-full bg-foreground/30 shrink-0 mt-2" />
                    {item}
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Documents */}
      <section className="py-10 sm:py-16 px-4 border-y border-border bg-muted/30">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Documentazione</p>
            <h2 className="text-2xl sm:text-3xl font-light tracking-tight mb-3">Documenti per Strutture Sanitarie</h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {documents.map((doc) => (
              <Card 
                key={doc.name} 
                className="p-3 sm:p-4 flex items-center justify-between gap-3 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => doc.link && navigate(doc.link)}
              >
                <div className="min-w-0">
                  <p className="font-normal text-sm truncate">{doc.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{doc.description}</p>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="py-10 sm:py-16 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Governance</p>
            <h2 className="text-2xl sm:text-3xl font-light tracking-tight">Ruoli e Responsabilità</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card className="p-4 sm:p-6">
              <div className="mb-4 pb-3 border-b border-border">
                <h3 className="font-medium text-sm sm:text-base">Clinica / Medico</h3>
                <span className="text-[10px] text-muted-foreground tracking-wide">Art. 4(7) — Titolare</span>
              </div>
              <ul className="space-y-2 text-xs sm:text-sm">
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-foreground/30 shrink-0 mt-2" />
                  Determina finalità e mezzi del trattamento
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-foreground/30 shrink-0 mt-2" />
                  Informa i pazienti sul trattamento
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-foreground/30 shrink-0 mt-2" />
                  Gestisce le richieste degli interessati
                </li>
              </ul>
            </Card>

            <Card className="p-4 sm:p-6">
              <div className="mb-4 pb-3 border-b border-border">
                <h3 className="font-medium text-sm sm:text-base">Refertius</h3>
                <span className="text-[10px] text-muted-foreground tracking-wide">Art. 28 — Responsabile</span>
              </div>
              <ul className="space-y-2 text-xs sm:text-sm">
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-foreground/30 shrink-0 mt-2" />
                  Tratta i dati solo su istruzioni documentate
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-foreground/30 shrink-0 mt-2" />
                  Garantisce riservatezza del personale
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-foreground/30 shrink-0 mt-2" />
                  Implementa misure di sicurezza adeguate
                </li>
              </ul>
            </Card>

            <Card className="p-4 sm:p-6 sm:col-span-2 lg:col-span-1">
              <div className="mb-4 pb-3 border-b border-border">
                <h3 className="font-medium text-sm sm:text-base">Sub-responsabili</h3>
                <span className="text-[10px] text-muted-foreground tracking-wide">Art. 28(2) GDPR</span>
              </div>
              <ul className="space-y-1.5 text-xs sm:text-sm">
                {[
                  'Daily — televisita cifrata non registrata, audio/video a Francoforte con riserva a Londra (Regno Unito adeguato), link 60 minuti; dati tecnici negli Stati Uniti per 3 giorni, Data Privacy Framework e SCC Modulo 3 come riserva; DPA art. 28 firmato il 30/10/2026',
                  'Cloudflare — rete, CDN e hosting tramite Lovable, inclusi dati in transito; catena contrattuale da verificare',
                  'Supabase — database, storage, autenticazione e infrastruttura; progetto in regione UE',
                  'Deepgram — trascrizione audio primaria (DPA firmato 04/08/2026, SCC)',
                  'OpenAI Ireland Ltd — trascrizione di riserva, elaborazione del testo e sintesi del teleconsulto (DPA firmato 29/08/2026, SCC)',
                  'Anthropic — elaborazione del testo (DPA nei Commercial Terms, 24/02/2025, SCC)',
                  'Google (Gemini) — lettura di carta intestata, logo e firma caricati dal medico; non riceve dati dei pazienti',
                  'Resend — link al paziente senza PDF allegato; copia al medico con allegato (DPA nei termini, 31/12/2025, SCC + EU-US DPF)',
                  'Sentry — monitoraggio degli errori tecnici del browser, configurato per non ricevere contenuti clinici',
                  'Stripe — pagamenti e fatturazione, distinto dal trattamento clinico',
                ].map((sp) => (
                  <li key={sp} className="flex items-start gap-2">
                    <div className="w-1 h-1 rounded-full bg-foreground/30 shrink-0 mt-2" />
                    {sp}
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Data Breach */}
      <section className="py-10 sm:py-16 px-4 border-y border-border bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Art. 33-34 GDPR</p>
            <h2 className="text-2xl sm:text-3xl font-light tracking-tight mb-3">Procedura Data Breach</h2>
            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              Protocollo di risposta strutturato in conformità ai requisiti normativi
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { step: '01', title: 'Rilevazione', time: 'Immediata', desc: 'Monitoraggio 24/7 con alerting automatico' },
              { step: '02', title: 'Valutazione', time: '< 4 ore', desc: 'Analisi impatto e classificazione gravità' },
              { step: '03', title: 'Notifica Autorità', time: '< 72 ore', desc: 'Comunicazione al Garante Privacy' },
              { step: '04', title: 'Notifica Interessati', time: 'Senza ritardo', desc: 'Se rischio elevato per i diritti' },
            ].map((item) => (
              <Card key={item.step} className="p-3 sm:p-4 text-center">
                <span className="text-lg sm:text-xl font-light text-muted-foreground/25 block mb-2 tracking-wide">
                  {item.step}
                </span>
                <h4 className="font-medium mb-1 text-sm">{item.title}</h4>
                <span className="text-[10px] text-muted-foreground tracking-wide">{item.time}</span>
                <p className="text-xs text-muted-foreground mt-2">{item.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Referente privacy */}
      <section className="py-10 sm:py-16 px-4">
        <div className="container mx-auto max-w-3xl">
          <Card className="p-6 sm:p-8 text-center">
            <Mail className="h-6 w-6 text-muted-foreground/40 mx-auto mb-4" />
            <h2 className="text-xl sm:text-2xl font-light mb-3 tracking-tight">Referente privacy</h2>
            <p className="text-sm text-muted-foreground mb-4 max-w-lg mx-auto">
              Per domande sulla privacy o esercizio dei diritti
            </p>
            <div className="space-y-1 mb-4 text-sm">
              <p>Giorgio Arghittu</p>
              <p><a href="mailto:privacy@refertius.com" className="text-muted-foreground hover:underline">privacy@refertius.com</a></p>
              <p><a href="mailto:giorgio@pec.refertius.com" className="text-muted-foreground hover:underline">giorgio@pec.refertius.com</a></p>
            </div>
            <p className="text-xs text-muted-foreground">
              Refertius gestisce le richieste senza ingiustificato ritardo e, in linea generale, entro un mese dal ricevimento, salvo i casi e le proroghe previste dalla normativa applicabile.
            </p>
          </Card>
        </div>
      </section>

      {/* CTA — dark, minimal */}
      <section className="py-12 sm:py-16 bg-foreground px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-2xl sm:text-3xl font-light text-background mb-3 tracking-tight">
            La sicurezza non è opzionale
          </h2>
          <p className="text-background/60 text-sm sm:text-base mb-6">
            Infrastruttura progettata per la protezione dei dati sanitari
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              size="lg" 
              variant="secondary" 
              className="text-sm sm:text-base"
              onClick={() => navigate('/auth?register=1')}
            >
              Inizia Gratis
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button 
              size="lg" 
              variant="ghost" 
              className="text-sm sm:text-base text-background hover:bg-background/10"
              onClick={() => navigate('/prezzi')}
            >
              Vedi i Piani
            </Button>
          </div>
        </div>
      </section>

      <section className="border-t border-border px-4 py-10">
        <div className="container mx-auto flex max-w-4xl flex-col items-center justify-center gap-3 text-center sm:flex-row sm:gap-4">
          <Button asChild variant="outline"><Link to="/trasparenza">Trasparenza</Link></Button>
          <Button asChild variant="outline"><Link to="/sicurezza">Sicurezza e infrastruttura</Link></Button>
        </div>
      </section>
      <PublicFooter />
      </div>
    </>
  );
}
